import i18next from "i18next";
import { useContext, createContext, useState, useEffect, useCallback, useMemo } from "react";
import Squares4Icon from "../../components/icons/Squares4Icon";
import authService from "../services/authService";
import cookieService from "../services/cookie";
import { loadPersistedData, persistData } from "../utils/persistor";
import { useProfileQuery, useProfileUpdateMutation } from "../services/react-query/useProfileQuery";
import { setAxiosLang } from "../services/langStore";

const DEFAULT_LANG = "en";
const GUEST_LANG_KEY = "guest_lang";

const context = createContext({
    main: {
        theme: "dark",
        setTheme: () => { },
        lang: DEFAULT_LANG,
        setLang: () => { },
        activeMenuIcon: Squares4Icon,
        setActiveMenuIcon: () => { }
    },
    profile: {
        token: null,
        setToken: () => { }
    },
    market: {
        coin: "btc",
        pair: "usdt"
    },
    setMarket: () => { }
});

export const useMainContext = () => {
    return useContext(context);
};

const getInitialLang = () => {
    return loadPersistedData(GUEST_LANG_KEY) || DEFAULT_LANG;
};

const MainContextProvider = ({ children }) => {
    const [market, _setMarket] = useState({ coin: "btc", pair: "usdt" });

    const [theme, setTheme] = useState(
        loadPersistedData("theme") || "dark"
    );

    // language (guest)
    const [lang, _setLang] = useState(getInitialLang);
    const [langReady, setLangReady] = useState(false);

    const [token, _setToken] = useState(authService.getToken());
    const [activeMenuIcon, setActiveMenuIcon] = useState(() => Squares4Icon);


    const { data: profile } = useProfileQuery({
        enabled: !!token
    });

    const { mutate: updateProfile } = useProfileUpdateMutation();

    useEffect(() => {
        persistData("theme", theme);

        if (theme === "dark") {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
    }, [theme]);


    useEffect(() => {
        i18next.changeLanguage(lang);
    }, [lang]);

    useEffect(() => {
        if (!profile) return;

        if (profile.lang) {
            _setLang(profile.lang);
        } else {
            updateProfile({ lang });
        }

        setLangReady(true);
    }, [profile]);

    const setLang = useCallback((newLang) => {
        if (newLang === lang) return;

        _setLang(newLang);
        if (token) {
            updateProfile({ lang: newLang });
            persistData(GUEST_LANG_KEY, null);
        } else {
            persistData(GUEST_LANG_KEY, newLang);
        }
    }, [lang, token, updateProfile]);


    const setToken = useCallback((token, internalNavigation = true) => {
        cookieService.set("token", token, { path: "/" });
        if (internalNavigation) _setToken(token);
    }, []);


    useEffect(() => {
        authService.registerLogout(() => {
            _setToken(null);

            _setLang(loadPersistedData(GUEST_LANG_KEY) || DEFAULT_LANG);
        });
    }, []);


    const setMarket = useCallback((market) => {
        _setMarket(market);
    }, []);

    useEffect(() => {
        if (!langReady) return;
        setAxiosLang(lang);
    }, [lang, langReady]);

    const contextValue = useMemo(() => ({
        main: {
            theme,
            setTheme,
            lang,
            setLang,
            activeMenuIcon,
            setActiveMenuIcon
        },
        profile: {
            token,
            setToken
        },
        market,
        setMarket
    }), [
        theme,
        setTheme,
        lang,
        setLang,
        activeMenuIcon,
        setActiveMenuIcon,
        token,
        setToken,
        market,
        setMarket
    ]);

    return (
        <context.Provider value={contextValue}>
            {children}
        </context.Provider>
    );
};

export default MainContextProvider;